<template>
  <v-card flat tile class="pdf-viewer-container">
    <v-card-text
      class="pa-0"
      :style="containerStyle"
    >
      <!-- Loader while source is being processed or loaded -->
      <div v-if="loading" class="pa-4 white" style="height: 100%">
        <v-skeleton-loader type="image" height="100%" />
      </div>

      <template v-else-if="processedSrc">
        <!-- Mobile Optimized View -->
        <div v-if="isMobile" class="pa-12 text-center d-flex flex-column align-center justify-center fill-height">
          <v-icon size="100" color="primary lighten-4">mdi-file-pdf-box</v-icon>
          <div class="mt-6 grey--text text--darken-2 headline font-weight-bold">
            {{ title }} Preview
          </div>
          <div class="mt-2 grey--text subtitle-1 mb-8">
            Your document is ready. Since you are on a mobile device, please use the button below to view it.
          </div>
          <v-btn
            x-large
            color="primary"
            class="text-none px-8 rounded-lg shadow-lg"
            @click="openPdfInNewTab"
          >
            <v-icon left>mdi-eye</v-icon>
            Open {{ title }}
          </v-btn>
          <div class="mt-4 grey--text caption">
            The document will open in your device's native PDF viewer.
          </div>
        </div>

        <!-- Desktop Iframe View -->
        <iframe
          v-else
          :src="processedSrc"
          width="100%"
          height="100%"
          frameborder="0"
          style="border: none; display: block"
          :title="title + ' Viewer'"
          loading="lazy"
          @load="loading = false"
        ></iframe>
      </template>

      <!-- Error State -->
      <div v-else class="pa-12 text-center d-flex flex-column align-center justify-center fill-height">
        <v-icon size="100" color="error lighten-4">mdi-alert-circle</v-icon>
        <div class="mt-6 grey--text text--darken-2 headline font-weight-bold">
          Unable to Load Document
        </div>
        <div class="mt-2 grey--text subtitle-1">
          There was an error loading your document. Please try again or contact support.
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: "AppPdfViewer",
  props: {
    src: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: "Document",
    },
    height: {
      type: String,
      default: "80vh",
    },
    minHeight: {
      type: String,
      default: "700px",
    }
  },
  data() {
    return {
      loading: true,
      processedSrc: null,
      blobUrl: null,
    };
  },
  computed: {
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      ) || (window.innerWidth <= 768);
    },
    containerStyle() {
      return {
        height: this.isMobile ? 'auto' : this.height,
        minHeight: this.isMobile ? '300px' : this.minHeight,
        position: 'relative',
        background: this.isMobile ? '#ffffff' : '#525659'
      };
    }
  },
  watch: {
    src: {
      immediate: true,
      handler(newSrc) {
        this.processSource(newSrc);
      }
    }
  },
  methods: {
    async processSource(src) {
      this.loading = true;
      try {
        // Clean up previous blob URL
        if (this.blobUrl) {
          URL.revokeObjectURL(this.blobUrl);
          this.blobUrl = null;
        }

        if (!src) {
          this.processedSrc = null;
          return;
        }

        // Handle base64
        if (src.startsWith('data:application/pdf;base64,') || !src.startsWith('http')) {
          let base64Data = src;
          if (!src.startsWith('data:application/pdf;base64,')) {
            base64Data = `data:application/pdf;base64,${src}`;
          }
          
          const blob = await this.base64ToBlob(base64Data);
          this.blobUrl = URL.createObjectURL(blob);
          this.processedSrc = this.blobUrl;
        } else {
          // Regular URL
          this.processedSrc = src;
        }
      } catch (error) {
        console.error("Error processing PDF source:", error);
        this.processedSrc = null;
      } finally {
        this.loading = false;
      }
    },
    async base64ToBlob(base64) {
      const parts = base64.split(';base64,');
      const contentType = parts[0].split(':')[1];
      const raw = window.atob(parts[1]);
      const rawLength = raw.length;
      const uInt8Array = new Uint8Array(rawLength);

      for (let i = 0; i < rawLength; ++i) {
        uInt8Array[i] = raw.charCodeAt(i);
      }

      return new Blob([uInt8Array], { type: contentType });
    },
    openPdfInNewTab() {
      if (!this.processedSrc) return;
      window.open(this.processedSrc, '_blank');
    }
  },
  beforeDestroy() {
    if (this.blobUrl) {
      URL.revokeObjectURL(this.blobUrl);
    }
  }
};
</script>

<style scoped>
.shadow-lg {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
}

.pdf-viewer-container {
  overflow: hidden;
}

/* Custom scrollbar for desktop iframe if needed */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
